import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { Box, Typography, Stack, Avatar } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import PersonIcon from '@mui/icons-material/Person';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { glass } from '../themes';

const STORAGE_KEY = 'onboarding_seen_v2';
const PAD = 14;

export interface OnboardingRefs {
  micRef: React.RefObject<HTMLDivElement | null>;
  patientsIconRef: React.RefObject<HTMLDivElement | null>;
  recordingsIconRef: React.RefObject<HTMLDivElement | null>;
  settingsIconRef: React.RefObject<HTMLDivElement | null>;
  addBtnRef: React.RefObject<HTMLDivElement | null>;
}

// ── Fake demo data ──────────────────────────────────────────────────────────

const FAKE_PATIENT = { initials: 'JA', name: 'Jane Austen', dob: 'Dec 16, 1815', notes: 'Chronic bilateral wrist pain' };
const FAKE_NOTE = '67-year-old female presenting with 3-month history of bilateral wrist pain, worse in the morning. Reports difficulty with fine motor tasks. No trauma. Family history of arthritis.';
const FAKE_SOAP = {
  S: '67F with 3-month history of bilateral wrist pain and morning stiffness, difficulty with fine motor tasks. No trauma. Family hx of arthritis.',
  O: 'Bilateral wrist tenderness on palpation, mild swelling. ROM reduced. Grip strength decreased bilaterally. No erythema.',
  A: 'Early osteoarthritis vs. rheumatoid arthritis. Autoimmune workup indicated.',
  P: 'Order RF, anti-CCP, ESR, CRP. Start naproxen 500mg BID PRN. Refer to rheumatology. Follow up in 4 weeks.',
};
const FAKE_CODES = [
  { type: 'CPT', code: '99213', desc: 'Office visit, moderate complexity' },
  { type: 'ICD-10', code: 'M19.031', desc: 'Primary osteoarthritis, right wrist' },
];

// ── Demo scene components ───────────────────────────────────────────────────

function DemoCard({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{
      bgcolor: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.7)',
      borderRadius: '16px', backdropFilter: 'blur(12px)', p: 1.5,
    }}>
      {children}
    </Box>
  );
}

function ScenePatientList() {
  return (
    <Box sx={{ p: 1 }}>
      <Typography sx={{ mb: 1.5, color: glass.text, fontFamily: '"DM Serif Display", serif', fontSize: '1.3rem' }}>Patients</Typography>
      <DemoCard>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar sx={{ width: 40, height: 40, bgcolor: '#1e3a5f', fontSize: '0.9rem', fontWeight: 700 }}>JA</Avatar>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: glass.text }}>{FAKE_PATIENT.name}</Typography>
              <Typography sx={{ fontSize: '0.7rem', color: glass.textSecondary }}>Dec 16</Typography>
            </Stack>
            <Typography sx={{ fontSize: '0.78rem', color: glass.textSecondary }}>{FAKE_PATIENT.notes}</Typography>
          </Box>
        </Stack>
      </DemoCard>
      <Box sx={{ mt: 1, opacity: 0.35 }}>
        {[['RK', 'Robert Keats', 'Post-op follow-up'], ['EM', 'Elizabeth Morris', 'Hypertension mgmt']].map(([i, n, d]) => (
          <DemoCard key={n}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ opacity: 0.5 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: '#888', fontSize: '0.8rem' }}>{i}</Avatar>
              <Box><Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: glass.text }}>{n}</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: glass.textSecondary }}>{d}</Typography></Box>
            </Stack>
          </DemoCard>
        ))}
      </Box>
    </Box>
  );
}

function ScenePatientDetail() {
  return (
    <Box sx={{ p: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Avatar sx={{ width: 48, height: 48, bgcolor: '#1e3a5f', fontWeight: 700 }}>JA</Avatar>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: glass.text }}>{FAKE_PATIENT.name}</Typography>
          <Typography sx={{ fontSize: '0.8rem', color: glass.textSecondary }}>DOB {FAKE_PATIENT.dob}</Typography>
        </Box>
      </Stack>
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: glass.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>Encounters</Typography>
      <DemoCard>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: glass.text }}>Today's Visit</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: glass.textSecondary }}>Wrist pain follow-up</Typography>
          </Box>
          <ArrowForwardIcon sx={{ fontSize: 16, color: 'rgba(0,0,0,0.25)' }} />
        </Stack>
      </DemoCard>
    </Box>
  );
}

function SceneAddNote() {
  const [shown, setShown] = useState('');
  useEffect(() => {
    let i = 0;
    const id = setInterval(() => {
      i++;
      setShown(FAKE_NOTE.slice(0, i));
      if (i >= FAKE_NOTE.length) clearInterval(id);
    }, 18);
    return () => clearInterval(id);
  }, []);

  return (
    <Box sx={{ p: 1 }}>
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: glass.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>Add Note</Typography>
      <Box sx={{
        bgcolor: 'rgba(255,255,255,0.7)', borderRadius: '14px', p: 1.5, border: '1.5px solid rgba(30,58,95,0.15)',
        minHeight: 90,
      }}>
        <Typography sx={{ fontSize: '0.82rem', color: glass.text, lineHeight: 1.55 }}>
          {shown}<Box component="span" sx={{ display: 'inline-block', width: 2, height: '1em', bgcolor: '#1e3a5f', ml: '1px', animation: 'blink 1s step-end infinite', '@keyframes blink': { '50%': { opacity: 0 } }, verticalAlign: 'text-bottom' }} />
        </Typography>
      </Box>
      <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
        <Box sx={{ px: 2, py: 0.75, borderRadius: '10px', bgcolor: '#1e3a5f', display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <AutoAwesomeIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.8)' }} />
          <Typography sx={{ fontSize: '0.8rem', color: '#fff', fontWeight: 600 }}>Generate SOAP</Typography>
        </Box>
      </Box>
    </Box>
  );
}

function SceneSOAP() {
  const full = `S: ${FAKE_SOAP.S}\n\nO: ${FAKE_SOAP.O}\n\nA: ${FAKE_SOAP.A}\n\nP: ${FAKE_SOAP.P}`;
  const [shown, setShown] = useState('');
  useEffect(() => {
    let i = 0;
    const id = setInterval(() => {
      i += 3;
      setShown(full.slice(0, i));
      if (i >= full.length) clearInterval(id);
    }, 16);
    return () => clearInterval(id);
  }, []);

  return (
    <Box sx={{ p: 1 }}>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
        <AutoAwesomeIcon sx={{ fontSize: 14, color: '#1e3a5f' }} />
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: '0.06em' }}>SOAP Note</Typography>
      </Stack>
      <Box sx={{ bgcolor: 'rgba(255,255,255,0.7)', borderRadius: '14px', p: 1.5, border: '1px solid rgba(255,255,255,0.8)', minHeight: 100, mb: 1 }}>
        <Typography sx={{ fontSize: '0.78rem', color: glass.text, lineHeight: 1.6, whiteSpace: 'pre-line' }}>{shown}</Typography>
      </Box>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {FAKE_CODES.map(c => (
          <Box key={c.code} sx={{ px: 1, py: 0.4, borderRadius: '8px', bgcolor: 'rgba(30,58,95,0.08)', border: '1px solid rgba(30,58,95,0.12)' }}>
            <Typography sx={{ fontSize: '0.7rem', color: '#1e3a5f', fontWeight: 600 }}>{c.type}: {c.code}</Typography>
            <Typography sx={{ fontSize: '0.65rem', color: glass.textSecondary }}>{c.desc}</Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

// ── Step definitions ────────────────────────────────────────────────────────

type StepType = 'demo' | 'spotlight';

interface DemoStep {
  type: 'demo';
  Scene: React.ComponentType;
  label: string;
  title: string;
  description: string;
}

interface SpotlightStep {
  type: 'spotlight';
  refKey: keyof OnboardingRefs;
  Icon: React.ElementType;
  title: string;
  description: string;
  shape: 'circle' | 'rounded';
}

type Step = DemoStep | SpotlightStep;

const STEPS: Step[] = [
  {
    type: 'demo', Scene: ScenePatientList, label: 'Patients',
    title: 'Your Patients',
    description: 'All your patients are listed here. Tap any card to open their full record.',
  },
  {
    type: 'demo', Scene: ScenePatientDetail, label: 'Patient Detail',
    title: 'Patient Record',
    description: 'Inside a patient you can see all their encounters, SOAP notes, and billing codes.',
  },
  {
    type: 'demo', Scene: SceneAddNote, label: 'Add Note',
    title: 'Clinical Notes',
    description: 'Type or dictate a note for today\'s encounter, then tap Generate SOAP to let AI structure it.',
  },
  {
    type: 'demo', Scene: SceneSOAP, label: 'SOAP + Billing',
    title: 'AI-Generated SOAP',
    description: 'The AI structures your note into SOAP format and suggests CPT and ICD-10 billing codes automatically.',
  },
  {
    type: 'spotlight', refKey: 'patientsIconRef', Icon: PersonIcon, shape: 'circle',
    title: 'Patients Tab',
    description: 'Switch to your patient list here.',
  },
  {
    type: 'spotlight', refKey: 'recordingsIconRef', Icon: HeadphonesIcon, shape: 'circle',
    title: 'Recordings',
    description: 'All voice recordings are saved here. Assign unlinked ones to a patient or delete them.',
  },
  {
    type: 'spotlight', refKey: 'settingsIconRef', Icon: SettingsIcon, shape: 'circle',
    title: 'Settings',
    description: 'Choose your microphone input — handy if you use a Bluetooth device. Sign out here too.',
  },
  {
    type: 'spotlight', refKey: 'addBtnRef', Icon: AddIcon, shape: 'rounded',
    title: 'Add Patient',
    description: 'Add a new patient. Once inside a record it becomes a back arrow.',
  },
  {
    type: 'spotlight', refKey: 'micRef', Icon: MicIcon, shape: 'circle',
    title: 'Voice Commands',
    description: 'You can do everything in the app with your voice. Just tap the mic and speak:\n\n"Create patient Jane Austen"\n"Create a SOAP note for Jane Austen"\n"What encounters does Jane Austen have?"\n"Add a billing code to her last visit"\n"Find patient John Smith"',
  },
];

// ── Helpers ─────────────────────────────────────────────────────────────────

interface Rect { left: number; top: number; width: number; height: number }

function measureRef(ref: React.RefObject<HTMLElement | null>): Rect | null {
  const el = ref.current;
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { left: r.left, top: r.top, width: r.width, height: r.height };
}

// ── Main component ───────────────────────────────────────────────────────────

export default function OnboardingOverlay({ refs }: { refs: OnboardingRefs }) {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [exiting, setExiting] = useState(false);
  const [rect, setRect] = useState<Rect | null>(null);

  const current = STEPS[step];

  const measure = useCallback(() => {
    if (current.type !== 'spotlight') return;
    const ref = refs[current.refKey] as React.RefObject<HTMLElement | null>;
    setRect(measureRef(ref));
  }, [step, refs, current]);

  useEffect(() => {
    if (!localStorage.getItem(STORAGE_KEY)) {
      setTimeout(() => setVisible(true), 700);
    }
  }, []);

  useLayoutEffect(() => {
    if (visible && current.type === 'spotlight') measure();
    else setRect(null);
  }, [visible, measure, current]);

  useEffect(() => {
    if (!visible) return;
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [visible, measure]);

  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => {
      setVisible(false);
      localStorage.setItem(STORAGE_KEY, '1');
    }, 300);
  }, []);

  const next = useCallback(() => {
    if (step < STEPS.length - 1) setStep(s => s + 1);
    else dismiss();
  }, [step, dismiss]);

  if (!visible) return null;

  const isLast = step === STEPS.length - 1;
  const demoStepCount = STEPS.filter(s => s.type === 'demo').length;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        position: 'fixed', inset: 0, zIndex: 1000,
        opacity: exiting ? 0 : 1,
        transition: 'opacity 0.3s ease',
        pointerEvents: exiting ? 'none' : 'auto',
      }}
    >
      {current.type === 'demo' ? (
        <DemoOverlay
          step={current}
          stepIndex={step}
          total={STEPS.length}
          isLast={isLast}
          demoStepCount={demoStepCount}
          onNext={next}
          onSkip={dismiss}
        />
      ) : (
        <SpotlightOverlay
          step={current}
          stepIndex={step}
          total={STEPS.length}
          isLast={isLast}
          rect={rect}
          onNext={next}
          onSkip={dismiss}
          onBackdrop={dismiss}
        />
      )}
    </Box>
  );
}

// ── Demo overlay ─────────────────────────────────────────────────────────────

function DemoOverlay({ step, stepIndex, total, isLast, demoStepCount, onNext, onSkip }: {
  step: DemoStep; stepIndex: number; total: number; isLast: boolean; demoStepCount: number;
  onNext: () => void; onSkip: () => void;
}) {
  return (
    <Box sx={{
      position: 'fixed', inset: 0,
      bgcolor: 'rgba(10,20,40,0.82)',
      backdropFilter: 'blur(6px)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      px: 2, gap: 2,
    }}>
      {/* Fake app window */}
      <Box sx={{
        width: '100%', maxWidth: 360,
        bgcolor: 'rgba(230,240,255,0.92)',
        borderRadius: '24px',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
      }}>
        {/* Fake status bar */}
        <Box sx={{ bgcolor: 'rgba(30,58,95,0.06)', px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.7rem', color: 'rgba(0,0,0,0.35)', fontWeight: 600 }}>airscribe</Typography>
          <Stack direction="row" spacing={1}>
            {['Patients', 'Recordings', 'Settings'].map(t => (
              <Typography key={t} sx={{ fontSize: '0.65rem', color: t === 'Patients' ? '#1e3a5f' : 'rgba(0,0,0,0.25)', fontWeight: t === 'Patients' ? 600 : 400 }}>{t}</Typography>
            ))}
          </Stack>
        </Box>
        {/* Scene content */}
        <Box sx={{ p: 0.5, minHeight: 200 }}>
          <step.Scene />
        </Box>
      </Box>

      {/* Description card */}
      <Box sx={{
        width: '100%', maxWidth: 360,
        bgcolor: 'rgba(255,255,255,0.97)',
        borderRadius: '20px', p: 2.5,
        boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
      }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e3a5f', mb: 0.75 }}>{step.title}</Typography>
        <Typography sx={{ fontSize: '0.85rem', color: 'rgba(0,0,0,0.6)', lineHeight: 1.6, mb: 2 }}>{step.description}</Typography>
        <ProgressRow stepIndex={stepIndex} total={total} isLast={isLast} onNext={onNext} onSkip={onSkip} />
      </Box>
    </Box>
  );
}

// ── Spotlight overlay ─────────────────────────────────────────────────────────

function SpotlightOverlay({ step, stepIndex, total, isLast, rect, onNext, onSkip, onBackdrop }: {
  step: SpotlightStep; stepIndex: number; total: number; isLast: boolean;
  rect: Rect | null; onNext: () => void; onSkip: () => void; onBackdrop: () => void;
}) {
  const spotLeft = rect ? rect.left - PAD : 0;
  const spotTop = rect ? rect.top - PAD : 0;
  const spotW = rect ? rect.width + PAD * 2 : 0;
  const spotH = rect ? rect.height + PAD * 2 : 0;
  const spotCx = spotLeft + spotW / 2;
  const spotRadius = step.shape === 'circle' ? Math.max(spotW, spotH) / 2 : 12;

  const cardW = Math.min(320, window.innerWidth - 32);
  const arrowH = 14;
  const cardAbove = !rect || rect.top > window.innerHeight / 2;
  const cardLeft = rect ? Math.max(16, Math.min(window.innerWidth - cardW - 16, spotCx - cardW / 2)) : (window.innerWidth - cardW) / 2;

  return (
    <Box sx={{ position: 'fixed', inset: 0 }} onClick={onBackdrop}>
      {/* SVG mask overlay */}
      {rect && (
        <svg style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
          <defs>
            <mask id="sm">
              <rect width="100%" height="100%" fill="white" />
              {step.shape === 'circle'
                ? <circle cx={spotLeft + spotW / 2} cy={spotTop + spotH / 2} r={spotRadius} fill="black" />
                : <rect x={spotLeft} y={spotTop} width={spotW} height={spotH} rx={spotRadius} fill="black" />}
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(10,20,40,0.78)" mask="url(#sm)" />
          {step.shape === 'circle'
            ? <circle cx={spotLeft + spotW / 2} cy={spotTop + spotH / 2} r={spotRadius} fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
            : <rect x={spotLeft} y={spotTop} width={spotW} height={spotH} rx={spotRadius} fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />}
        </svg>
      )}
      {!rect && <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(10,20,40,0.78)' }} />}

      {/* Card */}
      {rect && (
        <Box
          onClick={e => e.stopPropagation()}
          sx={{
            position: 'fixed',
            left: cardLeft,
            ...(cardAbove
              ? { bottom: window.innerHeight - spotTop + arrowH + 8 }
              : { top: spotTop + spotH + arrowH + 8 }),
            width: cardW,
            bgcolor: 'rgba(255,255,255,0.97)',
            borderRadius: '20px', p: 2.5,
            boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
          }}
        >
          {/* Arrow */}
          <Box sx={{
            position: 'absolute',
            left: Math.max(16, Math.min(cardW - 30, spotCx - cardLeft - 8)),
            ...(cardAbove ? { bottom: -arrowH + 1 } : { top: -arrowH + 1 }),
            width: 0, height: 0,
            borderLeft: '8px solid transparent', borderRight: '8px solid transparent',
            ...(cardAbove
              ? { borderTop: `${arrowH}px solid rgba(255,255,255,0.97)` }
              : { borderBottom: `${arrowH}px solid rgba(255,255,255,0.97)` }),
          }} />

          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
            <Box sx={{
              width: 34, height: 34, borderRadius: '10px', flexShrink: 0,
              bgcolor: step.refKey === 'micRef' ? 'rgba(229,57,53,0.1)' : 'rgba(30,58,95,0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <step.Icon sx={{ fontSize: 18, color: step.refKey === 'micRef' ? '#e53935' : '#1e3a5f' }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e3a5f' }}>{step.title}</Typography>
          </Stack>

          <Typography sx={{ fontSize: '0.83rem', color: 'rgba(0,0,0,0.6)', lineHeight: 1.6, mb: 2, whiteSpace: 'pre-line' }}>
            {step.description}
          </Typography>

          <ProgressRow stepIndex={stepIndex} total={total} isLast={isLast} onNext={onNext} onSkip={onSkip} />
        </Box>
      )}
    </Box>
  );
}

// ── Shared progress row ───────────────────────────────────────────────────────

function ProgressRow({ stepIndex, total, isLast, onNext, onSkip }: {
  stepIndex: number; total: number; isLast: boolean; onNext: () => void; onSkip: () => void;
}) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Stack direction="row" spacing={0.75}>
        {STEPS.map((_, i) => (
          <Box key={i} sx={{
            width: i === stepIndex ? 16 : 5, height: 5, borderRadius: 3,
            bgcolor: i === stepIndex ? '#1e3a5f' : i < stepIndex ? 'rgba(30,58,95,0.35)' : 'rgba(0,0,0,0.12)',
            transition: 'width 0.25s ease, background 0.25s ease',
          }} />
        ))}
      </Stack>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Typography onClick={onSkip} sx={{ fontSize: '0.8rem', color: 'rgba(0,0,0,0.3)', cursor: 'pointer', '&:hover': { color: 'rgba(0,0,0,0.55)' } }}>
          Skip
        </Typography>
        <Box onClick={onNext} sx={{
          px: 2, py: 0.75, borderRadius: '10px',
          bgcolor: '#1e3a5f', color: '#fff',
          fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
          '&:hover': { bgcolor: '#162d4a' },
        }}>
          {isLast ? 'Got it' : 'Next →'}
        </Box>
      </Stack>
    </Stack>
  );
}
